import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GitBranch, Users as UsersIcon, Mail, Database, FileSearch, CheckCircle2, Rocket, Download, type LucideIcon, AlertCircle, Activity, GitCompare, CreditCard, Settings as SettingsIcon, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { Button } from './Button';
import { Spinner } from './Spinner';
import { toast } from '@/state/toast';
import { ExpertsBrigadePanel } from './ExpertsBrigadePanel';
import { WavesView } from './IntegrityTab';
import { useAuth } from '@/state/auth';
import type { Snapshot, RemarksDashboardResponse, Project, Tariff } from '@/types/api';
import type { TabId } from './TabBar';
import { canRedaction, tariffStateFromProject } from '@/lib/tariff';

type Section = 'project' | 'routing' | 'members' | 'experts' | 'email' | 'rag' | 'audit' | 'fz87' | 'launch' | 'designer' | 'progress' | 'compare' | 'tariff';

const NAV: { id: Section; label: string; icon: LucideIcon }[] = [
  { id: 'project',  label: 'Проект',                        icon: SettingsIcon },
  { id: 'routing',  label: 'Маршрутизация',                 icon: GitBranch },
  { id: 'members',  label: 'Участники',                     icon: UsersIcon },
  { id: 'experts',  label: 'Эксперты команды',              icon: UsersIcon },
  { id: 'email',    label: 'Электронная почта',             icon: Mail },
  { id: 'rag',      label: 'Каталог нормативов',            icon: Database },
  { id: 'audit',    label: 'Аудит',                         icon: FileSearch },
  { id: 'fz87',     label: 'Комплектность 87-ФЗ',           icon: CheckCircle2 },
  { id: 'launch',   label: 'Готовность к запуску',          icon: Rocket },
];

// Только для admin (координатор): ручная активация тарифа проекта.
const ADMIN_ONLY_NAV: { id: Section; label: string; icon: LucideIcon }[] = [
  { id: 'tariff',   label: 'Тариф проекта',                 icon: CreditCard },
];

// Вкладка «Работа с замечаниями» у заказчика = хаб цикла отработки:
// проектировщик (кому уходят) → ход отработки (где сейчас) → сравнение редакций
// (что исправлено). Остальные разделы настроек — сторона куратора.
const CUSTOMER_NAV: { id: Section; label: string; icon: LucideIcon }[] = [
  { id: 'designer', label: 'Проектировщик',      icon: UsersIcon },
  { id: 'progress', label: 'Ход отработки',       icon: Activity },
  { id: 'compare',  label: 'Сравнение редакций',  icon: GitCompare },
];

interface Props { projectId: string; snapshots?: Snapshot[]; onGoToTab?: (t: TabId) => void }

export function SettingsTab({ projectId, snapshots = [], onGoToTab }: Props) {
  // Вид кабинета определяет ПЕРЕКЛЮЧЁННАЯ роль (админ может смотреть глазами
  // заказчика); default_role — только запасной вариант, если переключения не было.
  const isCustomer = useAuth((s) => (s.activeRole || s.user?.default_role) === 'customer');
  const isAdmin = useAuth((s) => (s.activeRole || s.user?.default_role) === 'admin');
  // Куратор/ГИП видят NAV; admin дополнительно — «Тариф проекта»; заказчик — свой набор.
  const nav = isCustomer ? CUSTOMER_NAV : (isAdmin ? [...NAV, ...ADMIN_ONLY_NAV] : NAV);
  const [section, setSectionState] = useState<Section>(() => {
    const fallback: Section = isCustomer ? 'designer' : 'routing';
    try {
      const u = new URLSearchParams(window.location.search);
      const p = u.get('panel') as Section | null;
      const allowed = nav.map((n) => n.id);
      return p && allowed.includes(p) ? p : fallback;
    } catch { return fallback; }
  });
  const setSection = (s: Section) => {
    setSectionState(s);
    try {
      const u = new URL(window.location.href);
      if (s === 'routing') u.searchParams.delete('panel');
      else u.searchParams.set('panel', s);
      window.history.replaceState(null, '', u.toString());
    } catch { /* swallow */ }
  };
  // Роль догрузилась — если активный раздел не входит в доступный набор,
  // сбрасываем на дефолт роли (заказчик: designer/progress/compare).
  useEffect(() => {
    const allowed = nav.map((n) => n.id);
    if (!allowed.includes(section)) setSection(isCustomer ? 'designer' : 'routing');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomer, isAdmin, section]);
  return (
    <div>
      {/* У заказчика вкладка уже называется «Работа с замечаниями» — второй такой
          же заголовок внутри избыточен (каждый под-раздел имеет свой H4). Шапку
          показываем только куратору/админу, где разделов много. */}
      {!isCustomer && (
        <header style={{ marginBottom: 24 }}>
          <h3 className="font-heading text-ink" style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em' }}>Настройки проекта</h3>
          <p className="text-muted mt-1" style={{ fontSize: 13 }}>Маршрутизация замечаний, участники, интеграции</p>
        </header>
      )}
      <div className="grid grid-cols-12" style={{ gap: 24 }}>
        <aside className="col-span-3">
          <div className="bg-surface rounded-card border border-border" style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {nav.map((it) => {
              const Icon = it.icon;
              const active = section === it.id;
              return (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => setSection(it.id)}
                  className="w-full text-left rounded-btn flex items-center"
                  style={{
                    padding: '10px 14px', gap: 12,
                    background: active ? '#FFFFFF' : 'transparent',
                    border: active ? '1px solid #E5E7EB' : '1px solid transparent',
                    color: active ? '#0A1628' : '#6B7280',
                    fontWeight: active ? 600 : 500, fontSize: 14, cursor: 'pointer',
                  }}
                >
                  <Icon size={18} stroke={active ? '#F59E0B' : 'currentColor'} />
                  <span>{it.label}</span>
                </button>
              );
            })}
          </div>
        </aside>
        <section className="col-span-9">
          {section === 'project'  && <ProjectPanel projectId={projectId} />}
          {section === 'designer' && <DesignerPanel projectId={projectId} />}
          {section === 'progress' && <ProgressPanel projectId={projectId} onGoToTab={onGoToTab} />}
          {section === 'compare'  && <ComparePanel projectId={projectId} snapshots={snapshots} />}
          {section === 'routing' && <RoutingPanel projectId={projectId} />}
          {section === 'members' && <MembersPanel projectId={projectId} />}
          {section === 'experts' && <ExpertsBrigadePanel />}
          {section === 'email'   && <EmailPanel projectId={projectId} />}
          {section === 'rag'     && <RagPanel />}
          {section === 'audit'   && <AuditPanel projectId={projectId} />}
          {section === 'fz87'    && <Fz87Panel projectId={projectId} />}
          {section === 'launch'  && <LaunchPanel projectId={projectId} />}
          {section === 'tariff' && isAdmin && <TariffAdminPanel projectId={projectId} />}
        </section>
      </div>
    </div>
  );
}

/* ─── Building blocks ───────────────────────────────────────────────────── */
function Card({ children }: { children: React.ReactNode }) {
  return <div className="bg-white rounded-card border border-border" style={{ padding: 24 }}>{children}</div>;
}
function H4({ children, sub }: { children: React.ReactNode; sub?: string }) {
  return (
    <>
      <h4 className="font-heading text-ink" style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em' }}>{children}</h4>
      {sub && <p className="text-muted mt-1" style={{ fontSize: 13 }}>{sub}</p>}
    </>
  );
}
function KV({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div className="text-muted uppercase tracking-wider" style={{ fontSize: 11, fontWeight: 600 }}>{label}</div>
      <div className="text-ink mt-1" style={{ fontSize: 14, fontWeight: 500 }}>{value}</div>
    </div>
  );
}
function EmptyState({ children }: { children: React.ReactNode }) {
  return <p className="text-muted text-center" style={{ padding: '24px 0', fontSize: 13 }}>{children}</p>;
}

/* ─── Маршрутизация ────────────────────────────────────────────────────── */
// Backend: /projects/{id}/routing — реальные авто-назначения по разделам.
// Не путать с правилами маршрутизации (роут-rules), которых пока нет.
interface RoutingAssignment {
  section_code?: string;
  role?: string;
  user_email?: string;
  user_name?: string;
  due?: string | null;
}
interface RoutingResponse { assignments: RoutingAssignment[] }

function RoutingPanel({ projectId }: { projectId: string }) {
  const { data, isLoading, isError } = useQuery<RoutingResponse>({
    queryKey: ['routing', projectId],
    queryFn: () => api.get<RoutingResponse>(`/projects/${projectId}/routing`),
    retry: false,
  });
  const rows = data?.assignments || [];
  return (
    <Card>
      <H4 sub="Назначения экспертов по разделам проекта. Правила маршрутизации появятся в следующих версиях.">Маршрутизация замечаний</H4>
      {isLoading && <EmptyState>Загрузка…</EmptyState>}
      {isError && <EmptyState>Не удалось загрузить назначения</EmptyState>}
      {!isLoading && !isError && rows.length === 0 && (
        <EmptyState>
          Авто-назначения по разделам пока отсутствуют. Замечания распределяются вручную через кнопку «Передать» в карточке замечания.
        </EmptyState>
      )}
      {rows.length > 0 && (
        <>
          <div className="grid grid-cols-12 text-muted uppercase tracking-wider" style={{ fontSize: 11, fontWeight: 600, marginTop: 20, paddingBottom: 8, borderBottom: '1px solid #E5E7EB', gap: 12 }}>
            <div className="col-span-3">Раздел</div>
            <div className="col-span-3">Роль</div>
            <div className="col-span-4">Эксперт</div>
            <div className="col-span-2">Срок</div>
          </div>
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-12 items-center" style={{ padding: '12px 0', borderBottom: '1px solid #E5E7EB', fontSize: 13, gap: 12 }}>
              <div className="col-span-3 font-heading" style={{ fontWeight: 600 }}>{r.section_code || '—'}</div>
              <div className="col-span-3 text-muted">{r.role || '—'}</div>
              <div className="col-span-4 text-ink" style={{ fontWeight: 500 }}>{r.user_name || r.user_email || '—'}</div>
              <div className="col-span-2 tabular text-ink">{r.due || '—'}</div>
            </div>
          ))}
        </>
      )}
      <div title="Появится в следующих версиях">
        <Button variant="secondary" size="sm" disabled className="mt-4">+ Добавить правило</Button>
      </div>
    </Card>
  );
}

/* ─── Участники ───────────────────────────────────────────────────────── */
interface RealMember {
  id: string;
  email: string;
  full_name?: string | null;
  default_role?: string | null;
  expert_section?: string | null;
  role: string; // curator | expert | gip
  added_at?: string | null;
}
interface MembersResponse { members: RealMember[] }

const ROLE_LABEL: Record<string, string> = {
  curator: 'Куратор',
  expert: 'Эксперт',
  gip: 'ГИП',
};

function MembersPanel({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery<MembersResponse>({
    queryKey: ['auth-members', projectId],
    queryFn: () => api.get<MembersResponse>(`/projects/${projectId}/auth-members`),
    retry: false,
  });
  const members = data?.members || [];

  const removeMut = useMutation({
    mutationFn: (userId: string) => api.delete(`/projects/${projectId}/auth-members/${userId}`),
    onSuccess: () => {
      toast.success('Участник удалён из проекта');
      qc.invalidateQueries({ queryKey: ['auth-members', projectId] });
    },
    onError: (e) => toast.error((e as Error).message || 'Ошибка удаления'),
  });
  return (
    <Card>
      <div className="flex items-center justify-between">
        <H4 sub={members.length > 0 ? `${members.length} ${plural(members.length, 'участник', 'участника', 'участников')}` : 'Назначения через RBAC (curator / expert / gip)'}>
          Участники проекта
        </H4>
      </div>
      <div style={{ marginTop: 24 }}>
        {isLoading && <EmptyState>Загрузка…</EmptyState>}
        {isError && <EmptyState>Не удалось загрузить участников</EmptyState>}
        {!isLoading && !isError && members.length === 0 && (
          <EmptyState>
            Участники проекта появятся после первого назначения. Назначения добавляет администратор или куратор проекта.
          </EmptyState>
        )}
        {members.map((m, i) => {
          const name = m.full_name || m.email;
          const roleLabel = ROLE_LABEL[m.role] || m.role;
          const sub = m.role === 'expert' && m.expert_section ? `${roleLabel}: ${m.expert_section}` : roleLabel;
          const bg = ['#0A1628', '#F59E0B', '#10B981'][i % 3];
          const fg = bg === '#F59E0B' ? '#0A1628' : '#FFFFFF';
          return (
            <div key={m.id} className="flex items-center" style={{ padding: '12px 0', borderBottom: '1px solid #E5E7EB', gap: 16 }}>
              <span className="rounded-pill flex items-center justify-center tabular" style={{ width: 36, height: 36, background: bg, color: fg, fontWeight: 600, fontSize: 12 }}>{makeInit(name)}</span>
              <div style={{ flex: 1 }}>
                <div className="text-ink" style={{ fontSize: 14, fontWeight: 600 }}>{name}</div>
                <div className="text-muted mt-0.5" style={{ fontSize: 12 }}>{m.email}</div>
              </div>
              <span className="rounded-pill bg-surface" style={{ padding: '4px 10px', fontSize: 12, fontWeight: 500, color: '#0A1628' }}>{sub}</span>
              <button
                type="button"
                className="text-muted hover:text-red-600"
                onClick={() => {
                  if (confirm(`Удалить ${name} (${m.email}) из проекта? Назначения сохранятся, но пользователь потеряет доступ к этому проекту.`)) {
                    removeMut.mutate(m.id);
                  }
                }}
                disabled={removeMut.isPending}
                title="Удалить участника из проекта"
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}
              >×</button>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
function makeInit(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase() || '').join('');
}

/* ─── Проектировщик (Фаза 8, для заказчика) ───────────────────────────────── */
interface InviteResult { invited: boolean; assigned: boolean; invite_url?: string; emailed?: boolean }

// Этап 3: секция «Проект» — переименование + опасная зона (удаление).
// RBAC на бэке: admin или curator-владелец (PATCH/DELETE /projects/{id}).
function ProjectPanel({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data: project } = useQuery<Project>({
    queryKey: ['project', projectId],
    queryFn: () => api.get<Project>(`/projects/${projectId}`),
  });
  const [name, setName] = useState('');
  const [dirtyInit, setDirtyInit] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  // Сид имени из проекта (один раз, чтобы не затирать правку при рефетче).
  useEffect(() => {
    if (project && !dirtyInit) { setName(project.name); setDirtyInit(true); }
  }, [project, dirtyInit]);

  const rename = useMutation({
    mutationFn: (n: string) => api.patch<{ ok: boolean; name: string }>(`/projects/${projectId}`, { name: n }),
    onSuccess: (r) => {
      toast.success('Проект переименован');
      setName(r.name);
      qc.invalidateQueries({ queryKey: ['project', projectId] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось переименовать'),
  });

  const del = useMutation({
    mutationFn: () => api.delete<{ ok: boolean }>(`/projects/${projectId}`),
    onSuccess: () => {
      toast.success('Проект удалён');
      qc.invalidateQueries({ queryKey: ['projects'] });
      nav('/projects');
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось удалить'),
  });

  const trimmed = name.trim();
  const canSave = !!trimmed && trimmed !== (project?.name || '') && !rename.isPending;

  return (
    <div style={{ maxWidth: 640 }}>
      <h4 className="font-heading text-ink" style={{ fontSize: 16, fontWeight: 600 }}>Проект</h4>
      <p className="text-muted mt-1" style={{ fontSize: 13, marginBottom: 16 }}>
        Наименование и управление проектом. ID: {projectId.slice(0, 8)}
      </p>

      <label className="text-muted" style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>
        Наименование проекта
      </label>
      <div className="flex items-center" style={{ gap: 8 }}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={300}
          placeholder="Например: 7266/1 — Реконструкция…"
          style={{ flex: 1, fontSize: 14, padding: '9px 12px', border: '1px solid #E5E7EB', borderRadius: 8, outline: 'none', color: '#0A1628' }}
        />
        <Button variant="primary" size="sm" disabled={!canSave} onClick={() => rename.mutate(trimmed)}>
          {rename.isPending ? 'Сохраняю…' : 'Сохранить'}
        </Button>
      </div>

      {/* Опасная зона */}
      <div className="rounded-card" style={{ marginTop: 32, padding: 16, border: '1px solid rgba(239,68,68,.3)', background: 'rgba(239,68,68,.04)' }}>
        <div className="flex items-center" style={{ gap: 8, marginBottom: 6 }}>
          <AlertCircle size={16} stroke="#EF4444" />
          <span className="font-heading" style={{ fontSize: 14, fontWeight: 600, color: '#B91C1C' }}>Опасная зона</span>
        </div>
        <p className="text-muted" style={{ fontSize: 12.5, lineHeight: 1.5, marginBottom: 12 }}>
          Удаление проекта необратимо: удалятся документы, редакции, замечания и результаты проверок.
        </p>
        {!confirmDel ? (
          <button
            type="button"
            onClick={() => setConfirmDel(true)}
            className="inline-flex items-center rounded-btn"
            style={{ gap: 7, padding: '8px 14px', fontSize: 13, fontWeight: 600, background: '#fff', color: '#B91C1C', border: '1px solid #EF4444', cursor: 'pointer' }}
          >
            <Trash2 size={14} /> Удалить проект
          </button>
        ) : (
          <div className="flex items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
            <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>Удалить проект безвозвратно?</span>
            <button
              type="button"
              onClick={() => del.mutate()}
              disabled={del.isPending}
              className="inline-flex items-center rounded-btn"
              style={{ gap: 6, padding: '8px 14px', fontSize: 13, fontWeight: 600, background: '#EF4444', color: '#fff', border: 'none', cursor: 'pointer' }}
            >
              <Trash2 size={14} /> {del.isPending ? 'Удаляю…' : 'Да, удалить'}
            </button>
            <button
              type="button"
              onClick={() => setConfirmDel(false)}
              style={{ padding: '8px 12px', fontSize: 13, background: 'transparent', color: '#6B7280', border: '1px solid #E5E7EB', borderRadius: 8, cursor: 'pointer' }}
            >
              Отмена
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function DesignerPanel({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<MembersResponse>({
    queryKey: ['auth-members', projectId],
    queryFn: () => api.get<MembersResponse>(`/projects/${projectId}/auth-members`),
    retry: false,
  });
  const designers = (data?.members || []).filter((m) => m.role === 'gip');

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const [resetUrl, setResetUrl] = useState('');

  // Сброс пароля проектировщика (сценарий 2026-07-04): генерируем одноразовую
  // ссылку /reset/<токен>, показываем для копирования (авто-письма нет).
  const resetPwd = useMutation({
    mutationFn: (userId: string) =>
      api.post<{ reset_url: string }>(`/projects/${projectId}/members/${userId}/reset-link`, {}),
    onSuccess: (res) => {
      setResetUrl(res.reset_url);
      setInviteUrl('');
      toast.success('Ссылка сброса создана — скопируйте и передайте проектировщику');
    },
    onError: (e) => toast.error((e as Error).message || 'Не удалось создать ссылку сброса'),
  });

  const invite = useMutation({
    mutationFn: () =>
      api.post<InviteResult>(`/projects/${projectId}/invite-member`, {
        email: email.trim(),
        full_name: fullName.trim() || null,
        role: 'gip',
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['auth-members', projectId] });
      if (res.invited && res.invite_url) {
        setInviteUrl(res.invite_url);
        toast.success(res.emailed ? 'Приглашение отправлено на почту' : 'Приглашение создано — скопируйте ссылку');
      } else if (res.assigned) {
        toast.success('Проектировщик назначен на проект');
        setEmail(''); setFullName('');
      }
    },
    onError: (e) => toast.error((e as Error).message || 'Ошибка приглашения'),
  });

  return (
    <Card>
      <H4 sub="Пригласите проектировщика (ГИП). После приёмки замечания уходят ему — он отвечает и правит документацию.">Проектировщик</H4>

      <div style={{ marginTop: 20 }}>
        {isLoading && <EmptyState>Загрузка…</EmptyState>}
        {!isLoading && designers.length === 0 && <EmptyState>Проектировщик пока не назначен.</EmptyState>}
        {designers.map((m) => (
          <div key={m.id} className="flex items-center" style={{ padding: '12px 0', borderBottom: '1px solid #E5E7EB', gap: 16 }}>
            <span className="rounded-pill flex items-center justify-center" style={{ width: 36, height: 36, background: '#0A1628', color: '#FFFFFF', fontWeight: 600, fontSize: 12 }}>{makeInit(m.full_name || m.email)}</span>
            <div style={{ flex: 1 }}>
              <div className="text-ink" style={{ fontSize: 14, fontWeight: 600 }}>{m.full_name || m.email}</div>
              <div className="text-muted" style={{ fontSize: 12 }}>{m.email}</div>
            </div>
            <span className="rounded-pill bg-surface" style={{ padding: '4px 10px', fontSize: 12, fontWeight: 500, color: '#0A1628' }}>ГИП</span>
            <button
              type="button"
              onClick={() => resetPwd.mutate(m.id)}
              disabled={resetPwd.isPending}
              title="Сгенерировать ссылку для сброса пароля проектировщика"
              className="text-muted hover:text-ink"
              style={{ background: 'none', border: '1px solid #E5E7EB', borderRadius: 16, padding: '4px 12px', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              {resetPwd.isPending ? '…' : 'Сбросить пароль'}
            </button>
          </div>
        ))}
      </div>

      {resetUrl && (
        <div className="rounded-card" style={{ marginTop: 12, padding: 12, background: 'rgba(245,158,11,.07)', border: '1px solid rgba(245,158,11,.25)' }}>
          <div className="text-ink" style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Ссылка для сброса пароля (действует 24 часа, одноразовая):</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              readOnly
              value={resetUrl}
              onFocus={(e) => e.currentTarget.select()}
              style={{ flex: 1, padding: '6px 10px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 12, fontFamily: 'ui-monospace, monospace' }}
            />
            <Button variant="secondary" size="sm" onClick={() => { void navigator.clipboard.writeText(resetUrl); toast.success('Скопировано'); }}>Копировать</Button>
          </div>
          <div className="text-muted" style={{ fontSize: 11, marginTop: 6 }}>Передайте ссылку проектировщику — он откроет её и задаст новый пароль. Все его текущие сессии будут завершены.</div>
        </div>
      )}

      <div style={{ marginTop: 24 }}>
        <div className="text-ink" style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Пригласить по e-mail</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email проектировщика"
            style={{ flex: 1, minWidth: 200, padding: '8px 12px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 13 }}
          />
          <input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="ФИО (необязательно)"
            style={{ flex: 1, minWidth: 200, padding: '8px 12px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 13 }}
          />
          <Button variant="primary" size="sm" disabled={!email.trim() || invite.isPending} onClick={() => invite.mutate()}>
            {invite.isPending ? 'Отправляем…' : 'Пригласить'}
          </Button>
        </div>

        {inviteUrl && (
          <div className="rounded-card" style={{ marginTop: 12, padding: 12, background: 'rgba(37,99,235,.06)', border: '1px solid rgba(37,99,235,.2)' }}>
            <div className="text-ink" style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Ссылка для входа проектировщика (действует 7 дней):</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                readOnly
                value={inviteUrl}
                onFocus={(e) => e.currentTarget.select()}
                style={{ flex: 1, padding: '6px 10px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 12, fontFamily: 'ui-monospace, monospace' }}
              />
              <Button variant="secondary" size="sm" onClick={() => { void navigator.clipboard.writeText(inviteUrl); toast.success('Скопировано'); }}>Копировать</Button>
            </div>
            <div className="text-muted" style={{ fontSize: 11, marginTop: 6 }}>Передайте ссылку проектировщику. Если настроена почта — письмо также ушло на указанный e-mail.</div>
          </div>
        )}
      </div>

      <div className="text-muted" style={{ marginTop: 20, fontSize: 12, lineHeight: 1.5, paddingTop: 16, borderTop: '1px solid #E5E7EB' }}>
        Тип объекта, наименование и реквизиты заказчика определяются автоматически и хранятся в паспорте объекта — откройте его кликом по заголовку проекта вверху страницы.
      </div>
    </Card>
  );
}

/* ─── Ход отработки (для заказчика) ───────────────────────────────────────── */
// Сводка по циклу замечаний: сервер уже считает summary {open, awaiting_response,
// closed} в /remarks/dashboard — отдельный список не дублируем, только счётчики
// со ссылкой во вкладку «Замечания».
export function ProgressPanel({ projectId, onGoToTab }: { projectId: string; onGoToTab?: (t: TabId) => void }) {
  const { data, isLoading, isError } = useQuery<RemarksDashboardResponse>({
    queryKey: ['remarks-dashboard', projectId],
    queryFn: () => api.get<RemarksDashboardResponse>(`/projects/${projectId}/remarks/dashboard`),
    retry: false,
  });
  // 11.6: пока проектировщик не назначен, замечания не «переданы» — они ждут передачи.
  const membersQ = useQuery<MembersResponse>({
    queryKey: ['auth-members', projectId],
    queryFn: () => api.get<MembersResponse>(`/projects/${projectId}/auth-members`),
    retry: false,
  });
  const hasDesigner = (membersQ.data?.members || []).some((m) => m.role === 'gip');
  const s = data?.summary;
  const cards: { key: string; label: string; hint: string; value: number; color: string; bg: string }[] = [
    { key: 'open',    label: hasDesigner ? 'Ждут ответа' : 'Ждут передачи', hint: hasDesigner ? 'переданы проектировщику, ответа ещё нет' : 'проектировщик не назначен — пригласите его во вкладке «Проектировщик»', value: s?.open ?? 0,              color: '#B45309', bg: 'rgba(245,158,11,.1)' },
    { key: 'review',  label: 'На вашей проверке', hint: 'проектировщик ответил — примите или верните', value: s?.awaiting_response ?? 0, color: '#2563EB', bg: 'rgba(37,99,235,.1)' },
    { key: 'closed',  label: 'Закрыто',        hint: 'замечание снято',                          value: s?.closed ?? 0,            color: '#10B981', bg: 'rgba(16,185,129,.1)' },
  ];
  return (
    <Card>
      <H4 sub="Где сейчас замечания в цикле «передал проектировщику → он ответил → вы приняли». Подробности и действия — во вкладке «Замечания».">Ход отработки</H4>
      {isLoading && <EmptyState>Загрузка…</EmptyState>}
      {isError && <EmptyState>Не удалось загрузить сводку по замечаниям</EmptyState>}
      {!isLoading && !isError && (
        <>
          <div className="grid grid-cols-3" style={{ gap: 12, marginTop: 20 }}>
            {cards.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => onGoToTab?.('remarks')}
                className="text-left rounded-card"
                style={{ padding: 16, background: c.bg, border: '1px solid transparent', cursor: onGoToTab ? 'pointer' : 'default' }}
                title={onGoToTab ? 'Открыть во вкладке «Замечания»' : undefined}
              >
                <div className="font-heading tabular" style={{ fontSize: 28, fontWeight: 600, color: c.color }}>{c.value}</div>
                <div className="text-ink" style={{ fontSize: 13, fontWeight: 600, marginTop: 2 }}>{c.label}</div>
                <div className="text-muted" style={{ fontSize: 12, marginTop: 4, lineHeight: 1.4 }}>{c.hint}</div>
              </button>
            ))}
          </div>
          <div className="text-muted" style={{ marginTop: 16, fontSize: 12 }}>
            Всего замечаний: {s?.total ?? 0}.
            {onGoToTab && (
              <> {' '}<button type="button" onClick={() => onGoToTab('remarks')} className="text-ink" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 600, textDecoration: 'underline' }}>Открыть список замечаний →</button></>
            )}
          </div>
        </>
      )}
    </Card>
  );
}

/* ─── Сравнение редакций (для заказчика) ──────────────────────────────────── */
// Переехало из отдельной верхней вкладки. При одной редакции сравнивать нечего —
// показываем пустое состояние; иначе — тот же WavesView, что был на вкладке.
export function ComparePanel({ projectId, snapshots }: { projectId: string; snapshots: Snapshot[] }) {
  // 15.E: сравнение редакций доступно только на тарифе «Доведение до экспертизы» (full).
  const projectQ = useQuery<Project>({
    queryKey: ['project', projectId],
    queryFn: () => api.get<Project>(`/projects/${projectId}`),
    staleTime: 1000 * 30,
  });
  const compareAllowed = canRedaction(tariffStateFromProject(projectQ.data));
  if (!compareAllowed) {
    return (
      <Card>
        <H4 sub="Что проектировщик исправил между редакциями документации.">Сравнение редакций</H4>
        <EmptyState>
          Доступно на тарифе «Доведение до экспертизы». Активируйте его, чтобы сравнивать редакции документации.
        </EmptyState>
      </Card>
    );
  }
  // 11.1: считаем ВСЕ редакции, включая архивные — предыдущая всегда
  // инвалидируется при загрузке новой, «активных ≥2» не бывает в принципе.
  if (snapshots.length < 2) {
    return (
      <Card>
        <H4 sub="Что проектировщик исправил между редакциями документации.">Сравнение редакций</H4>
        <EmptyState>
          Пока в проекте одна редакция — сравнивать не с чем. Раздел заполнится, когда проектировщик пришлёт исправленную редакцию: здесь будет видно, какие тома добавились, изменились или удалились и как поменялись замечания.
        </EmptyState>
      </Card>
    );
  }
  return <WavesView projectId={projectId} snapshots={snapshots} />;
}

/* ─── Электронная почта ───────────────────────────────────────────────── */
// Backend GET /email/status → плоский shape:
//   { smtp_configured, imap_configured, smtp_host, imap_host, imap_poll_sec, email_domain }
interface EmailStatusFlat {
  smtp_configured: boolean;
  imap_configured: boolean;
  smtp_host?: string | null;
  imap_host?: string | null;
  imap_poll_sec?: number | null;
  email_domain?: string | null;
}

function EmailPanel({ projectId }: { projectId: string }) {
  const { data, isLoading } = useQuery<EmailStatusFlat>({
    queryKey: ['email-status'],
    queryFn: () => api.get<EmailStatusFlat>('/email/status'),
    retry: false,
  });
  const smtpConfigured = !!data?.smtp_configured;
  const imapConfigured = !!data?.imap_configured;
  const anyConfigured = smtpConfigured || imapConfigured;
  return (
    <Card>
      <H4 sub="Уведомления участникам, входящие ответы по PDF-вложениям">Интеграция с эл. почтой</H4>
      {isLoading && <EmptyState>Загрузка…</EmptyState>}
      {!isLoading && !anyConfigured && (
        <div className="mt-5 rounded-card" style={{ padding: 16, background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.2)', fontSize: 13, color: '#B45309' }}>
          Email-интеграция не настроена. Свяжитесь с администратором для настройки SMTP/IMAP.
        </div>
      )}
      <div className="grid grid-cols-2 mt-6" style={{ gap: 24 }}>
        <div>
          <div className="text-muted uppercase tracking-wider" style={{ fontSize: 11, fontWeight: 600 }}>Исходящая (SMTP)</div>
          <StatPill ok={smtpConfigured} okLabel="подключено" warnLabel="не настроено" className="mt-2" />
          {smtpConfigured && (
            <div className="mt-4">
              <KV label="Хост"   value={data?.smtp_host || 'не указан'} />
              <KV label="Домен"  value={data?.email_domain || 'не указан'} />
            </div>
          )}
        </div>
        <div>
          <div className="text-muted uppercase tracking-wider" style={{ fontSize: 11, fontWeight: 600 }}>Входящая (IMAP)</div>
          <StatPill ok={imapConfigured} okLabel="опрос активен" warnLabel="не настроено" warn className="mt-2" />
          {imapConfigured && (
            <div className="mt-4">
              <KV label="Хост"          value={data?.imap_host || 'не указан'} />
              <KV label="Период опроса" value={`${data?.imap_poll_sec || 120} сек`} />
            </div>
          )}
          {imapConfigured && (
            <Button variant="secondary" size="sm" className="mt-3" onClick={async () => {
              try { await api.post(`/projects/${projectId}/email/poll`); toast.success('Опрос запущен'); }
              catch (e) { toast.error(e instanceof Error ? e.message : 'Ошибка'); }
            }}>Опросить сейчас</Button>
          )}
        </div>
      </div>
    </Card>
  );
}

function StatPill({ ok, okLabel, warnLabel, warn, className }: { ok: boolean; okLabel: string; warnLabel: string; warn?: boolean; className?: string }) {
  const isOk = ok && !warn;
  return (
    <span
      className={'inline-flex items-center rounded-pill ' + (className || '')}
      style={{
        padding: '4px 10px', gap: 6, fontSize: 12, fontWeight: 600,
        background: isOk ? 'rgba(16,185,129,.1)' : 'rgba(245,158,11,.1)',
        color:      isOk ? '#10B981'             : '#B45309',
      }}
    >
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: isOk ? '#10B981' : '#F59E0B' }} />
      {isOk ? okLabel : warnLabel}
    </span>
  );
}

/* ─── RAG ──────────────────────────────────────────────────────────────── */
interface NormPreviewResponse {
  source_url: string;
  source_host: string;
  markdown: string;
  markdown_length: number;
  suggested_code: string;
  suggested_short_title: string;
  suggested_full_title: string;
  warning: string;
}

interface NormRegistryItem {
  id: string;
  code: string;
  short_title: string;
  full_title: string;
  chunks_count: number;
  ingested_at: string;
  is_active: boolean;
}

function RagPanel() {
  const qc = useQueryClient();
  const [url, setUrl] = useState('');
  const [preview, setPreview] = useState<NormPreviewResponse | null>(null);
  const [editedMarkdown, setEditedMarkdown] = useState('');
  const [code, setCode] = useState('');
  const [shortTitle, setShortTitle] = useState('');
  const [fullTitle, setFullTitle] = useState('');

  const registryQ = useQuery<{ norms: NormRegistryItem[] }>({
    queryKey: ['norms-registry'],
    queryFn: () => api.get('/norms'),
    refetchOnWindowFocus: false,
  });

  const previewMut = useMutation({
    mutationFn: () => api.post<NormPreviewResponse>('/admin/norms/preview-url', { url: url.trim() }),
    onSuccess: (data) => {
      setPreview(data);
      setEditedMarkdown(data.markdown);
      setCode(data.suggested_code || '');
      setShortTitle(data.suggested_short_title || '');
      setFullTitle(data.suggested_full_title || '');
      toast.success(`Распарсено ${data.markdown_length} символов. Проверьте текст ниже.`);
    },
    onError: (e) => toast.error((e as Error).message || 'Ошибка'),
  });

  const ingestMut = useMutation({
    mutationFn: () => api.post<{ norm_id: string; chunks_count: number; sections_count: number; took_seconds: number }>(
      '/admin/norms/ingest-text',
      {
        markdown: editedMarkdown,
        code: code.trim(),
        short_title: shortTitle.trim(),
        full_title: fullTitle.trim(),
        source_url: preview?.source_url,
      },
    ),
    onSuccess: (data) => {
      toast.success(`Норматив ${code} добавлен в каталог: ${data.chunks_count} chunks, ${data.sections_count} секций`);
      // Сброс формы + обновление списка
      setUrl(''); setPreview(null); setEditedMarkdown('');
      setCode(''); setShortTitle(''); setFullTitle('');
      qc.invalidateQueries({ queryKey: ['norms-registry'] });
    },
    onError: (e) => toast.error((e as Error).message || 'Ошибка ingest'),
  });

  const deleteMut = useMutation({
    mutationFn: (normCode: string) => api.delete(`/admin/norms/${encodeURIComponent(normCode)}`),
    onSuccess: (_, normCode) => {
      toast.success(`Норматив ${normCode} удалён из каталога`);
      qc.invalidateQueries({ queryKey: ['norms-registry'] });
    },
    onError: (e) => toast.error((e as Error).message || 'Ошибка удаления'),
  });

  const allowedHosts = ['normativ.kontur.ru', 'pravo.gov.ru', 'publication.pravo.gov.ru', 'docs.cntd.ru', 'protect.gost.ru', 'minstroyrf.gov.ru'];

  return (
    <Card>
      <H4 sub="Векторный индекс норм и проектной документации">Семантический поиск (RAG)</H4>

      <div style={{ marginTop: 24 }}>
        <h5 className="font-heading text-ink" style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
          Добавить норматив по URL
        </h5>
        <p className="text-muted" style={{ fontSize: 12, marginBottom: 12, lineHeight: 1.5 }}>
          Парсер скачает HTML-страницу норматива и преобразует в Markdown.
          Поддерживаются хосты: {allowedHosts.join(', ')}.
          {' '}<b>Куратор проверяет и редактирует</b> текст перед сохранением в каталог.
        </p>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://normativ.kontur.ru/document?moduleId=1&documentId=..."
            style={{ flex: 1, padding: '8px 12px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 13 }}
            disabled={previewMut.isPending}
          />
          <Button
            variant="primary"
            size="sm"
            onClick={() => previewMut.mutate()}
            disabled={!url.trim() || previewMut.isPending}
          >
            {previewMut.isPending ? 'Загружаем…' : 'Распарсить'}
          </Button>
        </div>

        {preview && (
          <div style={{ border: '1px solid #E2E8F0', borderRadius: 8, padding: 16, background: '#F8FAFC' }}>
            <div style={{ padding: 10, background: '#FEF9C3', borderRadius: 6, fontSize: 12, color: '#854D0E', marginBottom: 16 }}>
              ⚠ {preview.warning}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 4 }}>Код норматива *</label>
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="FZ-384, PP-87, SP-119.13330..."
                  style={{ width: '100%', padding: '6px 10px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 13 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 4 }}>Короткое название *</label>
                <input
                  value={shortTitle}
                  onChange={(e) => setShortTitle(e.target.value)}
                  placeholder="ФЗ-384"
                  style={{ width: '100%', padding: '6px 10px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 13 }}
                />
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 4 }}>Полное название *</label>
              <input
                value={fullTitle}
                onChange={(e) => setFullTitle(e.target.value)}
                placeholder="Технический регламент о безопасности зданий и сооружений"
                style={{ width: '100%', padding: '6px 10px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 13 }}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 4 }}>
                Распарсенный markdown ({editedMarkdown.length} симв.) — отредактируйте перед сохранением
              </label>
              <textarea
                value={editedMarkdown}
                onChange={(e) => setEditedMarkdown(e.target.value)}
                style={{
                  width: '100%', height: 400,
                  padding: 10, border: '1px solid #CBD5E1', borderRadius: 6,
                  fontFamily: 'ui-monospace, monospace', fontSize: 12, lineHeight: 1.5,
                  resize: 'vertical',
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="ghost" onClick={() => { setPreview(null); setEditedMarkdown(''); }}>
                Отмена
              </Button>
              <Button
                variant="primary"
                onClick={() => ingestMut.mutate()}
                disabled={!code.trim() || !shortTitle.trim() || !fullTitle.trim() || editedMarkdown.length < 100 || ingestMut.isPending}
              >
                {ingestMut.isPending ? 'Сохраняем…' : 'Сохранить в каталог'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Список загруженных нормативов */}
      <div style={{ marginTop: 32 }}>
        <h5 className="font-heading text-ink" style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
          Загруженные нормативы ({registryQ.data?.norms?.length ?? 0})
        </h5>
        {registryQ.isLoading && <Spinner size={20} className="text-muted" />}
        {!registryQ.isLoading && (registryQ.data?.norms?.length ?? 0) === 0 && (
          <p className="text-muted" style={{ fontSize: 12, padding: 12, background: '#F8FAFC', borderRadius: 8 }}>
            Каталог пуст. Добавьте первый норматив через форму выше.
          </p>
        )}
        {(registryQ.data?.norms ?? []).length > 0 && (
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #E2E8F0', color: '#64748B', textAlign: 'left' }}>
                <th style={{ padding: '8px 4px', fontWeight: 600 }}>Код</th>
                <th style={{ padding: '8px 4px', fontWeight: 600 }}>Название</th>
                <th style={{ padding: '8px 4px', fontWeight: 600, textAlign: 'right' }}>Чанков</th>
                <th style={{ padding: '8px 4px', fontWeight: 600 }}>Загружен</th>
                <th style={{ padding: '8px 4px', width: 40 }}>—</th>
              </tr>
            </thead>
            <tbody>
              {(registryQ.data?.norms ?? []).map(n => (
                <tr key={n.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                  <td style={{ padding: '8px 4px' }}>
                    <span style={{ padding: '2px 8px', borderRadius: 999, background: '#EEF2FF', color: '#4338CA', fontSize: 11, fontWeight: 600 }}>
                      {n.code}
                    </span>
                  </td>
                  <td style={{ padding: '8px 4px' }} title={n.full_title}>
                    <div style={{ fontWeight: 500 }}>{n.short_title}</div>
                    {n.full_title && n.full_title !== n.short_title && (
                      <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>
                        {n.full_title.length > 80 ? n.full_title.slice(0, 80) + '…' : n.full_title}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '8px 4px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#475569' }}>
                    {n.chunks_count}
                  </td>
                  <td style={{ padding: '8px 4px', color: '#64748B', fontSize: 12 }}>
                    {new Date(n.ingested_at).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                  </td>
                  <td style={{ padding: '8px 4px' }}>
                    <button
                      onClick={() => {
                        if (confirm(`Удалить норматив ${n.code} «${n.short_title}»? Все ${n.chunks_count} чанков будут удалены из RAG.`)) {
                          deleteMut.mutate(n.code);
                        }
                      }}
                      disabled={deleteMut.isPending}
                      title="Удалить норматив из каталога"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: 16, padding: 4 }}
                    >×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  );
}

/* ─── Аудит ────────────────────────────────────────────────────────────── */
interface AuditEvent {
  id: string;
  source?: string;
  event_type: string;
  actor_email?: string | null;
  project_id?: string | null;
  created_at: string | null;
  payload?: Record<string, unknown>;
  ip?: string | null;
}
interface AuditResponse { events: AuditEvent[] }

function AuditPanel({ projectId }: { projectId: string }) {
  const { data, isLoading, isError, refetch } = useQuery<AuditResponse>({
    queryKey: ['audit-events', projectId],
    queryFn: () => api.get<AuditResponse>(`/audit-events?limit=20&project_id=${encodeURIComponent(projectId)}`),
    retry: false,
  });
  const events = data?.events || [];
  return (
    <Card>
      <div className="flex items-center justify-between">
        <H4 sub="Все изменения по проекту с временем и автором">Журнал событий проекта</H4>
        <Button variant="secondary" size="sm" icon={<Download size={14} />} disabled>Экспорт CSV</Button>
      </div>
      <div className="mt-5">
        {isLoading && <EmptyState>Загрузка…</EmptyState>}
        {isError && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <p className="text-muted" style={{ fontSize: 13, marginBottom: 12 }}>Не удалось загрузить журнал событий</p>
            <Button variant="secondary" size="sm" onClick={() => refetch()}>Повторить</Button>
          </div>
        )}
        {!isLoading && !isError && events.length === 0 && (
          <EmptyState>Событий аудита по этому проекту пока нет.</EmptyState>
        )}
        {events.map((e) => {
          const dt = e.created_at ? new Date(e.created_at) : null;
          const timeLabel = dt ? dt.toLocaleString('ru-RU', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : '—';
          const tag = (e.source || e.event_type || '').split('_')[0] || '—';
          return (
            <div key={e.id} className="flex items-start" style={{ padding: '12px 0', borderBottom: '1px solid #E5E7EB', gap: 16 }}>
              <span className="text-muted tabular" style={{ fontSize: 12, minWidth: 90 }}>{timeLabel}</span>
              <div style={{ flex: 1 }}>
                <div className="text-ink" style={{ fontSize: 14 }}>{e.event_type}</div>
                <div className="text-muted mt-0.5" style={{ fontSize: 12 }}>{e.actor_email || 'system'}</div>
              </div>
              <span className="rounded-pill bg-surface text-muted" style={{ padding: '2px 10px', fontSize: 11 }}>{tag}</span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/* ─── Комплектность 87-ФЗ ─────────────────────────────────────────────── */
interface ProjectMeta { id: string; name: string; created_at: string | null; object_type?: 'areal' | 'linear' | null }

function Fz87Panel({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.get<ProjectMeta>(`/projects/${projectId}`),
  });
  // Phase 13: подсасываем passport, чтобы показать «определено автоматически».
  const { data: passportState } = useQuery({
    queryKey: ['passport-state', projectId],
    queryFn: () =>
      api.get<{ passport_json: { object_type?: { value: 'areal' | 'linear'; confidence: 'auto' | 'user' } } }>(
        `/projects/${projectId}/passport/state`,
      ),
  });
  const passportObjType = passportState?.passport_json?.object_type;
  const current = data?.object_type ?? '';
  const [value, setValue] = useState<'' | 'areal' | 'linear'>('');
  useEffect(() => { setValue((current as '' | 'areal' | 'linear') || ''); }, [current]);

  const save = useMutation({
    mutationFn: (object_type: 'areal' | 'linear' | null) =>
      api.post<{ ok: boolean }>(`/projects/${projectId}/object-type`, { object_type }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project', projectId] });
      qc.invalidateQueries({ queryKey: ['pp87-compliance', projectId] });
      qc.invalidateQueries({ queryKey: ['passport-state', projectId] });
      toast.success('Тип объекта сохранён');
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось сохранить'),
  });

  const dirty = (value || null) !== (current || null);
  const autoDetected = passportObjType?.confidence === 'auto' ? passportObjType.value : null;

  return (
    <Card>
      <H4 sub="Тип объекта по ПП 87 определяет эталонный состав разделов">Правила проверки комплектности по 87-ФЗ</H4>
      {isLoading ? (
        <EmptyState>Загрузка…</EmptyState>
      ) : (
        <>
          <label className="block mt-5">
            <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>Тип объекта</span>
            <select
              value={value}
              onChange={(e) => setValue(e.target.value as '' | 'areal' | 'linear')}
              className="w-full mt-2 bg-white text-ink border border-border rounded-btn focus:border-ink outline-none"
              style={{ padding: '10px 14px', fontSize: 14, maxWidth: 480 }}
            >
              <option value="">Не указан</option>
              <option value="areal">Площадной (капитальное строительство, Часть 2)</option>
              <option value="linear">Линейный (трубопровод, ж/д, авто, Часть 3)</option>
            </select>
            <span className="text-muted block mt-2" style={{ fontSize: 12 }}>
              Эталонный каталог из Постановления Правительства РФ № 87. После смены типа предыдущая сверка теряет актуальность — выполните её заново на вкладке «Запрос».
            </span>
            {autoDetected && !current && (
              <span className="block mt-2" style={{ fontSize: 12, color: '#92400e' }}>
                Определено автоматически из состава разделов: <b>{autoDetected === 'linear' ? 'линейный' : 'площадной'}</b>. Подтвердите выбором ниже или измените, если нужно.
              </span>
            )}
          </label>
          <div style={{ marginTop: 20 }}>
            <Button
              variant="primary"
              size="sm"
              disabled={!dirty || save.isPending}
              onClick={() => save.mutate(value === '' ? null : value)}
            >
              {save.isPending ? 'Сохранение…' : 'Сохранить'}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

/* ─── Тариф проекта (15.E, только admin) ──────────────────────────────── */
// Ручная активация тарифа: платёжного шлюза нет — координатор (admin)
// выставляет тариф после подтверждения оплаты вне продукта.
// POST /projects/{id}/tariff {tariff: 'express'|'full'|null}.
const TARIFF_OPTIONS: { value: string; label: string }[] = [
  { value: '',        label: 'Не активирован' },
  { value: 'express', label: 'Разовый прогон (19 500 ₽)' },
  { value: 'full',    label: 'Доведение до экспертизы (39 000 ₽)' },
];

function TariffAdminPanel({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<Project>({
    queryKey: ['project', projectId],
    queryFn: () => api.get<Project>(`/projects/${projectId}`),
    staleTime: 1000 * 30,
  });
  // '' в select ↔ null тариф в API.
  const current: string = data?.tariff ?? '';
  const [value, setValue] = useState<string>('');
  useEffect(() => { setValue(current); }, [current]);

  const save = useMutation({
    mutationFn: (tariff: Tariff) =>
      api.post<{ project_id: string; tariff: Tariff }>(`/projects/${projectId}/tariff`, { tariff }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['project', projectId] });
      const label = TARIFF_OPTIONS.find((o) => o.value === (res.tariff ?? ''))?.label ?? 'обновлён';
      toast.success(`Тариф проекта: ${label}`);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось сохранить тариф'),
  });

  const dirty = value !== current;

  return (
    <Card>
      <H4 sub="Ручная активация тарифа координатором после подтверждения оплаты. Платёжного шлюза в продукте нет.">Тариф проекта</H4>
      {isLoading ? (
        <EmptyState>Загрузка…</EmptyState>
      ) : (
        <>
          <label className="block mt-5">
            <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>Тариф проекта</span>
            <select
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-full mt-2 bg-white text-ink border border-border rounded-btn focus:border-ink outline-none"
              style={{ padding: '10px 14px', fontSize: 14, maxWidth: 480 }}
            >
              {TARIFF_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <span className="text-muted block mt-2" style={{ fontSize: 12, lineHeight: 1.5, maxWidth: 560 }}>
              «Разовый прогон» — по одному прогону каждой проверки без перезапуска, без
              исправленных редакций и сравнения. «Доведение до экспертизы» — без ограничений. «Не активирован» —
              проверки запускать нельзя (можно только грузить исходные документы).
            </span>
          </label>
          <div style={{ marginTop: 20 }}>
            <Button
              variant="primary"
              size="sm"
              disabled={!dirty || save.isPending}
              onClick={() => save.mutate(value === '' ? null : (value as Tariff))}
            >
              {save.isPending ? 'Сохранение…' : 'Сохранить тариф'}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

/* ─── Готовность к запуску (Scale-gate) ───────────────────────────────── */
interface ScaleGateCheck {
  key: string;
  title: string;
  status: 'pass' | 'warn' | 'fail';
  observed?: number | string | null;
  threshold?: number | string | null;
  detail?: string;
}
interface ScaleGateResponse {
  status: 'green' | 'amber' | 'red';
  checks: ScaleGateCheck[];
  blockers?: string[];
  warnings?: string[];
}

interface SnapshotsResponse { snapshots?: Array<{ id: string; invalidated?: boolean }> }
interface DocumentsResponse { documents?: Array<{ id: string; ingest_status?: string }> }
// Плоский/легаси shape, который читает LaunchPanel (unassigned-счётчики).
// Отличается от импортированного RemarksDashboardResponse (summary+remarks).
interface RemarksDashboardLite {
  open_total?: number;
  open_unassigned?: number;
  open?: number;
  unassigned?: number;
}

function LaunchPanel({ projectId }: { projectId: string }) {
  const gate = useQuery<ScaleGateResponse>({
    queryKey: ['scale-gate'],
    queryFn: () => api.get<ScaleGateResponse>('/meta/scale-gate'),
    retry: false,
  });
  const snapshots = useQuery<SnapshotsResponse>({
    queryKey: ['snapshots', projectId],
    queryFn: () => api.get<SnapshotsResponse>(`/projects/${projectId}/snapshots`),
    retry: false,
  });
  const documents = useQuery<DocumentsResponse>({
    queryKey: ['documents', projectId],
    queryFn: () => api.get<DocumentsResponse>(`/projects/${projectId}/documents`),
    retry: false,
  });
  const dashboard = useQuery<RemarksDashboardLite>({
    queryKey: ['remarks-dashboard', projectId],
    queryFn: () => api.get<RemarksDashboardLite>(`/projects/${projectId}/remarks/dashboard`),
    retry: false,
  });
  const email = useQuery<EmailStatusFlat>({
    queryKey: ['email-status'],
    queryFn: () => api.get<EmailStatusFlat>('/email/status'),
    retry: false,
  });

  type ItemState = 'ok' | 'warn' | 'pending';
  interface Item { state: ItemState; title: string; sub: string }
  const items: Item[] = [];

  // 1. Редакция зафиксирована — хотя бы один не-invalidated snapshot
  if (!snapshots.isLoading && !snapshots.isError) {
    const snaps = snapshots.data?.snapshots || [];
    const valid = snaps.filter((s) => !s.invalidated);
    if (valid.length > 0) {
      items.push({ state: 'ok', title: 'Редакция зафиксирована', sub: `${valid.length} ${plural(valid.length, 'снимок', 'снимка', 'снимков')}` });
    } else {
      items.push({ state: 'warn', title: 'Редакция не зафиксирована', sub: 'Создайте snapshot для фиксации текущего состояния' });
    }
  }

  // 2. Документы проанализированы — все ingest_status === 'ready'
  if (!documents.isLoading && !documents.isError) {
    const docs = documents.data?.documents || [];
    if (docs.length === 0) {
      items.push({ state: 'warn', title: 'Документы не загружены', sub: 'Загрузите проектную документацию' });
    } else {
      const ready = docs.filter((d) => d.ingest_status === 'ready').length;
      if (ready === docs.length) {
        items.push({ state: 'ok', title: 'Документы проанализированы', sub: `${ready} / ${docs.length} готово` });
      } else if (ready === 0) {
        items.push({ state: 'pending', title: 'Анализ документов в процессе', sub: `${ready} / ${docs.length} готово` });
      } else {
        items.push({ state: 'warn', title: 'Часть документов ещё в обработке', sub: `${ready} / ${docs.length} готово` });
      }
    }
  }

  // 3. Замечания распределены — нет open без assignee (если dashboard вернул unassigned)
  if (!dashboard.isLoading && !dashboard.isError && dashboard.data) {
    const d = dashboard.data;
    const unassigned = (d.open_unassigned ?? d.unassigned);
    if (typeof unassigned === 'number') {
      if (unassigned === 0) {
        items.push({ state: 'ok', title: 'Замечания распределены', sub: 'Все открытые замечания назначены' });
      } else {
        items.push({ state: 'warn', title: 'Есть нераспределённые замечания', sub: `${unassigned} ${plural(unassigned, 'замечание', 'замечания', 'замечаний')} без эксперта` });
      }
    }
  }

  // 4. Email-интеграция
  if (!email.isLoading && !email.isError) {
    if (email.data?.smtp_configured) {
      items.push({ state: 'ok', title: 'Email-интеграция активна', sub: email.data.imap_configured ? 'SMTP + IMAP' : 'SMTP подключён' });
    } else {
      items.push({ state: 'warn', title: 'Email-интеграция не настроена', sub: 'Свяжитесь с администратором' });
    }
  }

  // 5. Scale-gate (global): добавляем все проверки как пункты чек-листа.
  if (!gate.isLoading && !gate.isError && gate.data?.checks) {
    for (const c of gate.data.checks) {
      const state: ItemState = c.status === 'pass' ? 'ok' : c.status === 'warn' ? 'warn' : 'warn';
      items.push({ state, title: c.title, sub: c.detail || '' });
    }
  }

  const okCount = items.filter((i) => i.state === 'ok').length;
  const total = items.length;
  const loading = gate.isLoading || snapshots.isLoading || documents.isLoading || dashboard.isLoading || email.isLoading;

  const overallColor = total === 0 ? '#6B7280' : okCount === total ? '#10B981' : okCount * 2 >= total ? '#F59E0B' : '#B45309';

  return (
    <Card>
      <H4 sub="Контрольный лист зрелости проекта перед публикацией заключения">Готовность к промышленному запуску</H4>
      {loading && <EmptyState>Загрузка…</EmptyState>}
      {!loading && total > 0 && (
        <>
          <div className="rounded-card flex items-center mt-5" style={{ padding: 24, gap: 16, background: 'rgba(16,185,129,.05)', border: '1px solid rgba(16,185,129,.2)' }}>
            <CheckCircle2 size={40} stroke={overallColor} />
            <div style={{ flex: 1 }}>
              <div className="text-muted uppercase tracking-wider" style={{ fontSize: 11, fontWeight: 600 }}>Общая готовность</div>
              <div className="font-heading tabular mt-1" style={{ fontSize: 28, fontWeight: 600, color: overallColor }}>{okCount} / {total}</div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                {okCount === total ? 'все проверки пройдены' : `${total - okCount} ${plural(total - okCount, 'пункт', 'пункта', 'пунктов')} ${plural(total - okCount, 'требует', 'требуют', 'требуют')} внимания`}
              </div>
            </div>
          </div>
          <div className="mt-5">
            {items.map((it, i) => {
              const Icon = it.state === 'ok' ? CheckCircle2 : AlertCircle;
              const color = it.state === 'ok' ? '#10B981' : it.state === 'pending' ? '#6B7280' : '#F59E0B';
              const label = it.state === 'ok' ? 'выполнено' : it.state === 'pending' ? 'в процессе' : 'требует внимания';
              return (
                <div key={i} className="flex items-center" style={{ padding: '12px 0', borderBottom: '1px solid #E5E7EB', gap: 14 }}>
                  <Icon size={20} stroke={color} />
                  <div style={{ flex: 1 }}>
                    <div className="text-ink" style={{ fontSize: 14, fontWeight: 500 }}>{it.title}</div>
                    {it.sub && <div className="text-muted mt-0.5" style={{ fontSize: 12 }}>{it.sub}</div>}
                  </div>
                  <span className="rounded-pill" style={{ padding: '2px 10px', fontSize: 11, fontWeight: 600, background: it.state === 'ok' ? 'rgba(16,185,129,.1)' : it.state === 'pending' ? 'rgba(107,114,128,.1)' : 'rgba(245,158,11,.1)', color }}>
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
      {!loading && total === 0 && (
        <EmptyState>Нет данных для оценки готовности. Проверьте, что проект инициализирован.</EmptyState>
      )}
    </Card>
  );
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}
